import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Modal, Field, Select, Badge, Empty } from "@/components/Kit";
import { fmtDate } from "@/lib/date";
import { openPrint } from "@/lib/print";
import { Plus, Trash2, Undo2, AlertTriangle, Printer } from "lucide-react";

const inr = (n) => "₹" + (n || 0).toLocaleString("en-IN");
const COLOR = { issued: "#06B6D4", returned: "#10B981", lost: "#F43F5E" };

function slipHtml(school, student, list) {
  const rows = list.map((x, i) => `<tr>
      <td style="padding:4px 6px;border:1px solid #cbd5e1">${i + 1}</td>
      <td style="padding:4px 6px;border:1px solid #cbd5e1">${x.item_name} <span style="color:#64748b;font-size:10px">(${x.item_type})</span></td>
      <td style="padding:4px 6px;border:1px solid #cbd5e1;text-align:center">${x.qty}</td>
      <td style="padding:4px 6px;border:1px solid #cbd5e1;text-align:right">${inr(x.price)}</td>
      <td style="padding:4px 6px;border:1px solid #cbd5e1;text-align:right">${inr(x.price * x.qty)}</td>
    </tr>`).join("");
  const total = list.reduce((a, x) => a + x.price * x.qty, 0);
  return `<div class="page" style="width:148mm;min-height:105mm;padding:10mm;font-size:12px;color:#0f172a">
    <div style="display:flex;justify-content:space-between;border-bottom:2px solid #1e3a8a;padding-bottom:6px">
      <div><div style="font-family:Outfit,sans-serif;font-weight:800;font-size:16px">${school?.name || "School"}</div>
      <div style="color:#475569;font-size:10px">${school?.address || ""}${school?.phone ? " · " + school.phone : ""}</div></div>
      <div style="text-align:right"><div style="font-weight:800;color:#1e3a8a">BOOK ISSUE SLIP</div>
      <div style="font-size:10px;color:#64748b">${new Date().toLocaleDateString("en-GB")}</div></div>
    </div>
    <table style="width:100%;margin-top:8px;font-size:11px">
      <tr><td style="padding:2px 0"><b>Student:</b> ${student?.name || ""}</td><td style="padding:2px 0"><b>Adm. No:</b> ${student?.admission_no || ""}</td></tr>
      <tr><td style="padding:2px 0"><b>Class:</b> ${student?.class_name || ""}-${student?.section || ""}</td><td style="padding:2px 0"><b>Father:</b> ${student?.father_name || student?.parent_name || ""}</td></tr>
    </table>
    <table style="width:100%;border-collapse:collapse;margin-top:8px;font-size:11px">
      <thead><tr style="background:#eef2ff">
        <th style="padding:5px 6px;border:1px solid #cbd5e1;text-align:left">#</th>
        <th style="padding:5px 6px;border:1px solid #cbd5e1;text-align:left">Item</th>
        <th style="padding:5px 6px;border:1px solid #cbd5e1">Qty</th>
        <th style="padding:5px 6px;border:1px solid #cbd5e1;text-align:right">Rate</th>
        <th style="padding:5px 6px;border:1px solid #cbd5e1;text-align:right">Amount</th>
      </tr></thead>
      <tbody>${rows}
        <tr><td colspan="4" style="padding:5px 6px;border:1px solid #cbd5e1;text-align:right;font-weight:700">Total</td>
        <td style="padding:5px 6px;border:1px solid #cbd5e1;text-align:right;font-weight:800">${inr(total)}</td></tr>
      </tbody>
    </table>
    <div style="margin-top:22px;display:flex;justify-content:space-between;font-size:10px;color:#475569">
      <div>Received the above items in good condition.<br/><br/>______________________<br/>Parent / Student Signature</div>
      <div style="text-align:right">For ${school?.name || "School"}<br/><br/>______________________<br/>Issued By</div>
    </div>
  </div>`;
}

export default function IssuesTab() {
  const qc = useQueryClient();
  const { school } = useAuth();
  const [open, setOpen] = useState(false);
  const [studentId, setStudentId] = useState("");
  const [lines, setLines] = useState([{ item_id: "", qty: 1 }]);
  const [lostFor, setLostFor] = useState(null);
  const [fine, setFine] = useState("");
  const [status, setStatus] = useState("issued");
  const [q, setQ] = useState("");

  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/school/students")).data });
  const { data: items = [] } = useQuery({ queryKey: ["inv-items"], queryFn: async () => (await api.get("/school/inventory/items")).data });
  const { data: issues = [] } = useQuery({ queryKey: ["inv-issues"], queryFn: async () => (await api.get("/school/inventory/issues")).data });
  const refresh = () => ["inv-items", "inv-issues", "inv-report", "students"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const issue = useMutation({
    mutationFn: async () => (await api.post("/school/inventory/issues", { student_id: studentId, items: lines.filter((l) => l.item_id).map((l) => ({ item_id: l.item_id, qty: Number(l.qty) || 1 })) })).data,
    onSuccess: (d) => {
      toast.success(`${d.length} item(s) issued · parent notified`);
      const stu = students.find((s) => s.id === studentId);
      openPrint(slipHtml(school, stu, d), { title: `Book Issue Slip — ${stu?.name || ""}`, size: "148mm 105mm" });
      setOpen(false); setLines([{ item_id: "", qty: 1 }]); setStudentId(""); refresh();
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const ret = useMutation({
    mutationFn: async (id) => (await api.put(`/school/inventory/issues/${id}/return`)).data,
    onSuccess: () => { toast.success("Returned to stock"); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const lost = useMutation({
    mutationFn: async () => (await api.put(`/school/inventory/issues/${lostFor.id}/lost`, { fine: fine === "" ? null : Number(fine) })).data,
    onSuccess: (d) => { toast.success(`Marked lost · fine ${inr(d.fine)} added to fee dues`); setLostFor(null); setFine(""); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const student = students.find((s) => s.id === studentId);
  const suggested = (s) => items.filter((i) => !i.class_name || i.class_name === s?.class_name);
  const rows = issues.filter((i) => (!status || i.status === status) && (i.student_name.toLowerCase().includes(q.toLowerCase()) || i.admission_no.toLowerCase().includes(q.toLowerCase()) || i.item_name.toLowerCase().includes(q.toLowerCase())));

  return (
    <div data-testid="issues-tab">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {[["issued", "Issued"], ["returned", "Returned"], ["lost", "Lost"], ["", "All"]].map(([k, l]) => (
          <button key={k} onClick={() => setStatus(k)} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${status === k ? "bg-indigo-600 text-white" : "bg-slate-800/60 text-slate-400 hover:text-white"}`} data-testid={`iss-filter-${k || "all"}`}>{l}</button>
        ))}
        <input className="ep-input !w-56" placeholder="Search student / item" value={q} onChange={(e) => setQ(e.target.value)} data-testid="iss-search" />
        <button className="ep-btn ml-auto" onClick={() => setOpen(true)} data-testid="issue-btn"><Plus size={16} /> Issue Books</button>
      </div>
      <div className="ep-card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-4 py-3">Student</th><th className="px-4 py-3">Item</th><th className="px-4 py-3 text-right">Qty</th><th className="px-4 py-3">Issued On</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Fine</th><th className="px-4 py-3"></th></tr></thead>
          <tbody>
            {rows.map((i) => (
              <tr key={i.id} className="border-b border-slate-800/50 hover:bg-slate-800/30" data-testid={`issue-row-${i.id}`}>
                <td className="px-4 py-2.5"><p className="font-medium text-white">{i.student_name}</p><p className="font-mono text-[11px] text-slate-500">{i.admission_no} · {i.class_name}-{i.section}</p></td>
                <td className="px-4 py-2.5 text-slate-300">{i.item_name} <span className="text-[11px] text-slate-500">({i.item_type})</span></td>
                <td className="px-4 py-2.5 text-right font-mono">{i.qty}</td>
                <td className="px-4 py-2.5 text-xs text-slate-400">{fmtDate(i.issued_at)}</td>
                <td className="px-4 py-2.5"><Badge color={COLOR[i.status]}>{i.status}</Badge></td>
                <td className="px-4 py-2.5 text-right text-rose-400">{i.fine ? inr(i.fine) : "—"}</td>
                <td className="px-4 py-2.5">
                  <div className="flex justify-end gap-1.5">
                    <button
                      onClick={() => openPrint(slipHtml(school, students.find((s) => s.id === i.student_id) || { name: i.student_name, admission_no: i.admission_no, class_name: i.class_name, section: i.section }, [i]), { title: `Book Issue Slip — ${i.student_name}`, size: "148mm 105mm" })}
                      className="rounded-lg bg-indigo-500/15 p-2 text-indigo-300 hover:bg-indigo-500/25" title="Print slip" data-testid={`slip-${i.id}`}
                    >
                      <Printer size={14} />
                    </button>
                    {i.status === "issued" && (
                      <>
                        <button onClick={() => ret.mutate(i.id)} className="rounded-lg bg-emerald-500/15 p-2 text-emerald-300 hover:bg-emerald-500/25" title="Return" data-testid={`return-${i.id}`}><Undo2 size={14} /></button>
                        <button onClick={() => { setLostFor(i); setFine(String(i.price * i.qty)); }} className="rounded-lg bg-rose-500/15 p-2 text-rose-400 hover:bg-rose-500/25" title="Mark lost" data-testid={`lost-${i.id}`}><AlertTriangle size={14} /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7}><Empty>No records</Empty></td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Issue Notebooks / Textbooks" testid="issue-modal">
        <Select label="Student *" value={studentId} onChange={(e) => setStudentId(e.target.value)} data-testid="iss-student"><option value="">Select student…</option>{students.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.class_name}-{s.section}) · {s.admission_no}</option>)}</Select>
        <div className="mt-3 space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_80px_auto] gap-2" data-testid={`iss-line-${i}`}>
              <select className="ep-input" value={l.item_id} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, item_id: e.target.value } : x)))} data-testid={`iss-item-${i}`}>
                <option value="">Select item…</option>
                {suggested(student).map((it) => <option key={it.id} value={it.id} disabled={it.stock < 1}>{it.name} · stock {it.stock} · {inr(it.price)}</option>)}
              </select>
              <input type="number" min={1} className="ep-input" value={l.qty} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} data-testid={`iss-qty-${i}`} />
              <button onClick={() => setLines(lines.filter((_, j) => j !== i))} disabled={lines.length === 1} className="text-slate-500 hover:text-rose-400 disabled:opacity-30"><Trash2 size={15} /></button>
            </div>
          ))}
          <button className="ep-btn ep-btn-ghost !py-1.5 text-xs" onClick={() => setLines([...lines, { item_id: "", qty: 1 }])} data-testid="iss-add-line"><Plus size={13} /> Add another item</button>
        </div>
        <button className="ep-btn mt-5 w-full" disabled={issue.isPending || !studentId || !lines.some((l) => l.item_id)} onClick={() => issue.mutate()} data-testid="iss-save-btn">{issue.isPending ? "Issuing…" : "Issue & Notify Parent"}</button>
      </Modal>

      <Modal open={!!lostFor} onClose={() => setLostFor(null)} title={`Mark Lost — ${lostFor?.item_name || ""}`} testid="lost-modal">
        <p className="mb-3 text-sm text-slate-400">Student: <b className="text-white">{lostFor?.student_name}</b>. The fine is added to the student's fee dues and the parent is notified.</p>
        <Field label="Fine amount (₹)" type="number" value={fine} onChange={(e) => setFine(e.target.value)} data-testid="lost-fine" />
        <button className="ep-btn mt-5 w-full !bg-rose-600 hover:!bg-rose-500" disabled={lost.isPending} onClick={() => lost.mutate()} data-testid="lost-confirm-btn">{lost.isPending ? "Saving…" : "Confirm Lost & Add Fine"}</button>
      </Modal>
    </div>
  );
}
